// Design agent: Spec 013 — a single reusable "Export" dropdown mounted once per tool (the inspector
// panel, the VLSM plan), offering the three flat-file/clipboard formats sysadmins actually asked for:
// a downloadable CSV, a tab-separated clipboard copy that pastes as spreadsheet columns, and a
// BIND-style zone text blob. Kept generic (headers/rows/subnets are handed in as plain data) so the
// same component serves both very different data shapes without knowing anything about IPv4 math.
// Developer agent: All the actual formatting (escaping, zone-name math) lives in
// `src/utils/exporters.js`, which is unit tested directly; this component only wires that pure logic
// to the three browser-only side effects (Blob download, clipboard write) that are hard to unit test.
import { useEffect, useRef, useState } from 'react';
import { toCsv, toTsv, toBindRecords, buildDateStamp } from '../utils/exporters';
import './ExportMenu.css';

// Design agent: Centralises the "how do we get text onto the user's clipboard" question in one
// place, since the modern async Clipboard API is not available in every browsing context (e.g. a
// non-HTTPS origin, or an older browser) and needs a graceful fallback rather than a thrown error.
// Developer agent: Tries `navigator.clipboard.writeText` first; if it is missing or rejects, falls
// back to the classic hidden-textarea + `document.execCommand('copy')` trick. Returns a boolean
// instead of throwing so the caller can show a friendly success/failure message either way.
async function copyTextToClipboard(text) {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // Design agent: Fall through to the legacy fallback below rather than surfacing this error —
    // clipboard permission prompts/denials are common and not actionable by the user here.
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const succeeded = document.execCommand('copy');
    document.body.removeChild(textarea);
    return succeeded;
  } catch (error) {
    return false;
  }
}

// Design agent: Triggers a real file download from purely client-side text — no server round trip —
// using the standard Blob + temporary anchor pattern the spec calls for.
// Developer agent: Revokes the object URL immediately after the click so the browser can reclaim the
// memory; the anchor element itself is never actually inserted visibly, only briefly attached to the
// DOM because some browsers refuse to `.click()` a detached element.
function downloadTextFile(filename, textContent, mimeType) {
  const blob = new Blob([textContent], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

// Design agent: Renders the "Export" glass dropdown described in spec 013. `csvRows`/`csvHeaders`
// drive both the CSV download and the TSV clipboard copy; `bindSubnets` (when present) drives the
// "Copy BIND records" action — a caller that has nothing BIND-shaped to export (there currently is
// none, but future tools might) can simply omit it and that menu item is disabled instead of erroring.
// Developer agent: `id` must be unique per mount (the inspector and the VLSM card each mount their
// own instance) so every element below keeps a unique, descriptive DOM id as required by CLAUDE.md.
function ExportMenu({ id, filenamePrefix, csvHeaders, csvRows, bindSubnets = null }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hostnamePattern, setHostnamePattern] = useState('{name}-{index}');
  const [statusMessage, setStatusMessage] = useState('');
  const containerRef = useRef(null);

  // Design agent: A dropdown left open when the user clicks anywhere else on the page feels broken;
  // closing it on any outside click matches how every other menu on the page (e.g. the belong-mode
  // popovers) already behaves.
  // Developer agent: Listens on `mousedown` (rather than `click`) so the outside-click check runs
  // before a click on another interactive element inside the same render fires its own handler.
  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMenuOpen]);

  // Design agent: Status messages ("CSV downloaded", "Copy failed…") are transient confirmations,
  // not a permanent log — they should fade away on their own instead of accumulating.
  useEffect(() => {
    if (statusMessage === '') {
      return undefined;
    }
    const timer = setTimeout(() => setStatusMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  const hasRows = Array.isArray(csvRows) && csvRows.length > 0;
  const hasBindSubnets = Array.isArray(bindSubnets) && bindSubnets.length > 0;

  // Design agent: Spec 013 item 4 — the CSV filename carries today's date so repeated exports never
  // silently overwrite each other in the user's downloads folder.
  const handleDownloadCsv = () => {
    if (!hasRows) {
      setStatusMessage('Nothing to export yet');
      return;
    }
    const csvContent = toCsv(csvHeaders, csvRows);
    downloadTextFile(`${filenamePrefix}-${buildDateStamp()}.csv`, csvContent, 'text/csv;charset=utf-8;');
    setStatusMessage('CSV downloaded');
  };

  const handleCopySpreadsheet = async () => {
    if (!hasRows) {
      setStatusMessage('Nothing to export yet');
      return;
    }
    const tsvContent = toTsv(csvHeaders, csvRows);
    const didCopy = await copyTextToClipboard(tsvContent);
    setStatusMessage(didCopy ? 'Copied — paste into a spreadsheet' : 'Copy failed — check clipboard permissions');
  };

  const handleCopyBindRecords = async () => {
    if (!hasBindSubnets) {
      setStatusMessage('Nothing to export yet');
      return;
    }
    const bindText = toBindRecords(bindSubnets, { hostnamePattern });
    const didCopy = await copyTextToClipboard(bindText);
    setStatusMessage(didCopy ? 'BIND records copied' : 'Copy failed — check clipboard permissions');
  };

  return (
    <div className="export-menu" id={id} ref={containerRef}>
      <button
        type="button"
        className="ghost-button export-menu-trigger"
        id={`${id}-trigger`}
        onClick={() => setIsMenuOpen((previous) => !previous)}
        aria-expanded={isMenuOpen}
        aria-haspopup="true"
      >
        Export ▾
      </button>
      {isMenuOpen && (
        <div className="export-menu-panel" id={`${id}-panel`} role="menu">
          <button
            type="button"
            className="export-menu-item"
            id={`${id}-download-csv`}
            onClick={handleDownloadCsv}
            disabled={!hasRows}
            role="menuitem"
          >
            Download CSV
          </button>
          <button
            type="button"
            className="export-menu-item"
            id={`${id}-copy-spreadsheet`}
            onClick={handleCopySpreadsheet}
            disabled={!hasRows}
            role="menuitem"
          >
            Copy for spreadsheet
          </button>
          <div className="export-menu-divider" id={`${id}-divider`} />
          <label className="export-menu-pattern-label" htmlFor={`${id}-pattern-input`} id={`${id}-pattern-label`}>
            BIND hostname pattern
          </label>
          <input
            type="text"
            className="export-menu-pattern-input"
            id={`${id}-pattern-input`}
            value={hostnamePattern}
            onChange={(event) => setHostnamePattern(event.target.value)}
            placeholder="{name}-{index}"
          />
          <button
            type="button"
            className="export-menu-item"
            id={`${id}-copy-bind`}
            onClick={handleCopyBindRecords}
            disabled={!hasBindSubnets}
            role="menuitem"
          >
            Copy BIND records
          </button>
          {statusMessage !== '' && (
            <p className="export-menu-status" id={`${id}-status`}>
              {statusMessage}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ExportMenu;
